import client from "./client";

// Models mirror Android: app/src/main/java/com/project/obj/notification/

export interface NotiIcon {
  rounded?: boolean | null;
  url: string | null;
}

export interface NotiAction {
  code: string | null;
  href: string | null;
  name: string | null;
}

export interface LocalizationTran {
  locale: string | null;
  text: string | null;
}

export interface LocalizationKey {
  id?: number | null;
  text: string | null;
  locale?: string | null;
  trans?: LocalizationTran[] | null;
}

// Android RemoteNotiStatus: NEW | READ
export type RemoteNotiStatus = "NEW" | "READ";

// Android NotiCategoryEnum.FOLLOWER_INV
export const NOTI_CATEGORY_FOLLOWER_INV = "follower-invitation";

export interface RemoteNoti {
  id: number;
  href: string | null;
  icon: NotiIcon | null;
  message: LocalizationKey | null;
  status: RemoteNotiStatus | null;
  extraInfo: Record<string, unknown> | null;
  actions: NotiAction[] | null;
  category: string | null;
  createdAt: string | null;
  updatedAt?: string | null;
  visible?: boolean | null;
}

export interface NotiBadge {
  notiCount: number | null;
  pairChatNotiCount: number | null;
}

// Android LocalizationKey.getTextByLocale: trans entry for the locale, else base text
export function getLocalizedText(
  message: LocalizationKey | null | undefined,
  locale: string,
): string {
  if (!message) return "";
  const tran = message.trans?.find((t) => t.locale === locale);
  return tran?.text ?? message.text ?? "";
}

export async function getNotifications(
  pageNum: number,
  pageSize: number,
): Promise<RemoteNoti[]> {
  const res = await client.get<{ result: RemoteNoti[] }>("/api/notifications", {
    params: { pageNum, pageSize },
  });
  return res.data.result;
}

export async function getNotificationBadge(): Promise<NotiBadge> {
  const res = await client.get<{ result: NotiBadge }>(
    "/api/notifications/badge",
  );
  return res.data.result;
}

export async function clearNotificationBadge(): Promise<void> {
  await client.delete("/api/notifications/badge");
}

export async function markNotificationRead(id: number): Promise<void> {
  await client.patch(`/api/notifications/${id}`, { status: "READ" });
}

export const NOTIFICATIONS_FALLBACK_PATH = "/notifications";

/**
 * Maps a notification href (main-api sends relative paths like "post/1/comment/2")
 * to an in-app path. Returns null for empty, scheme-bearing or protocol-relative hrefs,
 * and for hrefs with a backslash (browsers read "/\\evil.com" as "//evil.com"),
 * leading/trailing whitespace or ASCII control characters.
 * Must mirror hrefToUrl in public/sw-notification-routing.js
 */
export function notiHrefToPath(href: string | null | undefined): string | null {
  if (!href) return null;
  // eslint-disable-next-line no-control-regex
  if (href !== href.trim() || /[\\\x00-\x1f\x7f]/.test(href)) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//")) return null;
  return href.startsWith("/") ? href : `/${href}`;
}

/**
 * Maps the FCM data field `deeplink` to an in-app path: a same-origin http(s) URL (path + search + hash),
 * an app path, or `whoisit://app/<path>`. Anything else is null.
 * Must mirror deeplinkToUrl in public/sw-notification-routing.js
 */
export function notiDeeplinkToPath(deeplink: string | null | undefined): string | null {
  if (!deeplink) return null;
  // eslint-disable-next-line no-control-regex
  if (deeplink !== deeplink.trim() || /[\\\x00-\x1f\x7f]/.test(deeplink)) return null;
  if (/^whoisit:\/\/app\//i.test(deeplink))
    return notiHrefToPath(deeplink.replace(/^whoisit:\/\/app\//i, "/"));
  if (/^https?:\/\//i.test(deeplink)) {
    try {
      const u = new URL(deeplink);
      if (u.origin !== window.location.origin) return null;
      return notiHrefToPath(u.pathname + u.search + u.hash);
    } catch {
      return null;
    }
  }
  return notiHrefToPath(deeplink);
}
